import { v4 as uuidv4 } from 'uuid';
import React from 'react';
import { Button, Form, FormGroup, Input, Label } from '../../../components/Bootstrap';
import { NobleModal } from '../../../components';
import { MenuCategoryEditor } from './MenuCategoryEditor';
import { SalesCategory, MenuCategory } from './types';
import { transformCategoriesForAPI, canSubmitCategories } from './utils';
import { MenuCategoryInput } from '../../../globalTypes';

interface AddModalProps {
  open: boolean;
  saleCategories: SalesCategory[];
  toggle: () => void;
  onSubmit: (data: { name: string; menuCategories: MenuCategoryInput[] }) => void;
}

interface AddModalNameInputProps {
  onChangeName(name: string);
  name: string;
}

const AddModalNameInput: React.FC<AddModalNameInputProps> = ({ name, onChangeName }) => {
  const onChange = React.useCallback(
    (e) => {
      onChangeName(e.target.value);
    },
    [onChangeName]
  );

  return (
    <div className='mt-4'>
      <FormGroup>
        <Label>Menu name</Label>
        <Input onChange={onChange} value={name} />
      </FormGroup>
    </div>
  );
};

type Step = 'name' | 'categories';

export const AddModal: React.FC<AddModalProps> = ({ saleCategories, toggle, onSubmit, open }) => {
  const [step, setStep] = React.useState<Step>('name');
  const [name, setName] = React.useState('');

  const onClickBack = React.useCallback(() => {
    if (step === 'name') {
      toggle();
    } else {
      setStep('name');
    }
  }, [step, setStep, toggle]);

  const [categories, setCategories] = React.useState<MenuCategory[]>([
    {
      tempId: uuidv4(),
      name: '',
      salesCategoryId: null,
    },
  ]);

  const [hasTriedSubmitting, setHasTriedSubmitting] = React.useState(false);

  const onSubmitWrapped = React.useCallback(
    (e) => {
      e.preventDefault();

      if (step === 'name' && !!name) {
        setStep('categories');
      } else if (step === 'categories') {
        if (!canSubmitCategories(categories)) {
          setHasTriedSubmitting(true);
          return;
        }

        onSubmit({
          name,
          menuCategories: transformCategoriesForAPI(categories),
        });
      }
    },
    [onSubmit, name, step, setStep, categories]
  );

  const title = step === 'name' ? 'Add a menu' : 'Add menu categories';
  const description =
    step === 'name'
      ? 'Creating a new menu allows you to build custom menus for specific events or fulfillment stations.'
      : 'Customize your menu with custom categories.';

  return (
    <NobleModal size='lg' isOpen={open} toggle={toggle}>
      <NobleModal.Title title={title} />
      <NobleModal.Body>
        <p className='menu-modal-desc text-center'>{description}</p>
        <Form onSubmit={onSubmitWrapped} className='edit-menu-category-content'>
          {step === 'name' && <AddModalNameInput name={name} onChangeName={setName} />}
          {step === 'categories' && (
            <div className='mt-4'>
              <MenuCategoryEditor
                hasTriedSubmitting={hasTriedSubmitting}
                saleCategories={saleCategories}
                categories={categories}
                onChange={setCategories}
              />
            </div>
          )}
          <div className='text-center menu-modal-btns'>
            <Button type='button' className='me-4' color='link' onClick={onClickBack}>
              {step === 'name' ? 'Cancel' : 'Back'}
            </Button>{' '}
            <Button type='submit' color='primary'>
              {step === 'name' ? 'Next' : 'Save'}
            </Button>
          </div>
        </Form>
      </NobleModal.Body>
    </NobleModal>
  );
};
